import { LoadingService } from '@/core/services/loading.service';
import { User } from '@/core/models/user';
import { UserService } from '@/core/services/open-api/user.service';
import { AuthService } from '@/core/services/auth.service';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CrmWebhookEvent, CrmWebhookService } from '@/core/services/crm-webhook.service';
import moment from 'moment';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StdToasts, ToastService } from '@/core/services/toast.service';

@Component({
    selector: 'app-login-page',
    templateUrl: './login.component.html',
    // styleUrls: ['./login.component.scss'],
    standalone: false,
})
export class LoginComponent implements OnInit {
    loginForm: FormGroup;
    ssoLogin = false;
    displayValidationMessages = false;

    constructor(
        private readonly crmWebhookService: CrmWebhookService,
        private readonly loadingService: LoadingService,
        private readonly authService: AuthService,
        private readonly toastService: ToastService,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly titleService: Title,
        private fb: FormBuilder,
    ) {
        // Infos about email validation: https://www.emailregex.com/
        // legacy pattern: /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
        this.loginForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            password: [''],
            // remember: [false],
        });
    }

    get emailControl(): AbstractControl {
        return this.loginForm.get('email')!;
    }

    ngOnInit(): void {
        this.authService.loggedIn$.subscribe((isLoggedIn) => {
            if (!isLoggedIn) {
                // set meta title to "timeBuzzer Login" for SSO reasons
                this.titleService.setTitle('timeBuzzer Login');

                const token = this.router.parseUrl(this.router.url).queryParams['sso']; // avoid snapshot (old code), because that does not get updated!
                if (token) {
                    this.ssoLogin = true;
                    LocalStorageService.removeToken();

                    if (token.length < 200) {
                        this.authService.ssoLogin(token).subscribe({
                            next: (_token: string) => {},
                            error: (_error: void) => {
                                this.ssoLogin = false;
                                void this.router.navigate(['/']);
                                this.toastService.errorNoTranslate('Error', 'Anmeldung fehlgeschlagen');
                            },
                            complete: () => {
                                this.toastService.successNoTranslate('Success', 'Anmeldung erfolgreich');
                            },
                        });
                    } else {
                        LocalStorageService.storeToken(token);
                        this.authService.setLoggedIn(true);
                    }
                }
                return;
            }

            this.userService.loadCurrentUser(true).subscribe((user: User) => {
                if (user.isNew()) {
                    void this.router.navigate(['wizard']);
                    this.loadingService.setLoading(false);
                } else {
                    this.crmLog(user);
                    this.start();
                }
            });
        });
    }

    private start(): void {
        this.userService.loadAccount(true).subscribe((account) => {
            const url = account?.accountSettings?.onboarding !== 'COMPLETED' ? 'onboarding' : '';
            void this.router.navigate([url]);
        });
    }

    login(): void {
        if (this.emailControl.invalid) {
            this.displayValidationMessages = true;
            return;
        }
        this.loadingService.setLoading(true);
        const email: string = this.loginForm.get('email')?.value;
        const password: string = this.loginForm.get('password')?.value;

        if (!email || !password) {
            this.toastService.error('standard_error', 'public_login_error_missing_input');
            this.loadingService.setLoading(false);
            return;
        }

        this.authService.login(email, password, true /* remember */).subscribe({
            next: (_token: string) => {
                this.loadingService.setLoading(false);
            },
            error: (err: HttpErrorResponse) => {
                switch (err.error.message) {
                    case "Password doesn't match":
                        this.toastService.error('standard_error', 'public_login_error_password');
                        break;
                    case "Cannot read property 'password' of undefined":
                        this.toastService.toast(StdToasts.STANDARD_ERROR);
                        break;
                    case 'User not found':
                        this.toastService.error('standard_error', 'public_login_error_user');
                        break;
                    default:
                        console.error(err);
                        this.toastService.toast(StdToasts.STANDARD_ERROR);
                }
                this.loadingService.setLoading(false);
            },
        });
    }

    private crmLog(user: User): void {
        void this.crmWebhookService.send(CrmWebhookEvent.updateProfile, {
            email: user?.email,
            cloud_account_id: user?.accountId,
            last_login_webconsole: moment().format('YYYY-MM-DD'),
        });
    }
}
